import { Avatar, Flex, List, Radio, Tag, Typography } from 'antd'
import { CommentIndicator } from 'features/libra/comments/CommentIndicator'
import {
  getFieldSource,
  unwrapFieldValue,
  wrapFieldValue,
} from 'features/libra/ontology/ontologyFieldDefaults'
import {
  FieldCategory,
  FieldValues,
  InputField,
  OntologyField,
} from 'features/libra/ontology/ontologyFieldTypes'
import { FormSection } from 'features/libra/sections/formSection/formSectionTypes'
import { DynamicFieldTooltip } from 'features/libra/stepComponents/niiScreening/tooltips/DynamicFieldTooltip'
import { useComponentScope } from 'features/libra/workspace/ComponentScopeContext'
import { FC, useCallback, useMemo } from 'react'

interface QuestionListSectionProps {
  config: FormSection
  ontologyFields: Record<string, OntologyField>
  fieldValues: FieldValues
  setFieldValues?: (
    update: FieldValues | ((previous: FieldValues) => FieldValues)
  ) => void
  stepId: string
}

export const QuestionListSection: FC<QuestionListSectionProps> = ({
  config,
  ontologyFields,
  fieldValues,
  setFieldValues,
  stepId,
}) => {
  const componentId = useComponentScope()?.componentId
  const fields = useMemo(
    () =>
      config.fields.flatMap((fieldId) => {
        const field = ontologyFields[fieldId]
        return field?.category === FieldCategory.INPUT &&
          field.type === 'boolean'
          ? [{ fieldId, field: field as InputField }]
          : []
      }),
    [config.fields, ontologyFields]
  )

  const setQuestionValue = useCallback(
    (fieldId: string, value: boolean) => {
      setFieldValues?.((previous) => ({
        ...previous,
        [fieldId]: wrapFieldValue(value, getFieldSource(previous[fieldId])),
      }))
    },
    [setFieldValues]
  )

  return (
    <Flex vertical gap='middle'>
      <List
        bordered
        dataSource={fields}
        renderItem={({ fieldId, field }, index) => {
          const value = unwrapFieldValue<boolean>(fieldValues[fieldId]) ?? false
          const answer = setFieldValues ? (
            <Radio.Group
              aria-label={field.name}
              value={value}
              optionType='button'
              buttonStyle='solid'
              onChange={(event) =>
                setQuestionValue(fieldId, event.target.value as boolean)
              }
              options={[
                { label: 'Yes', value: true },
                { label: 'No', value: false },
              ]}
            />
          ) : (
            <Tag color={value ? 'warning' : 'default'}>
              {value ? 'Yes' : 'No'}
            </Tag>
          )

          return (
            <CommentIndicator
              key={fieldId}
              anchor={{
                type: 'field',
                stepId,
                fieldId,
                ...(componentId && { componentId }),
              }}>
              <List.Item actions={[answer]}>
                <List.Item.Meta
                  avatar={<Avatar size='small'>{index + 1}</Avatar>}
                  title={
                    <Flex align='start' gap='small'>
                      <Typography.Text strong>{field.name}</Typography.Text>
                      {field.tooltip && (
                        <DynamicFieldTooltip
                          tooltip={field.tooltip}
                          spaceLeft={false}
                        />
                      )}
                    </Flex>
                  }
                />
              </List.Item>
            </CommentIndicator>
          )
        }}
      />
    </Flex>
  )
}
